import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  RadioGroup,
  Radio,
  Checkbox,
  FormGroup,
  Avatar,
  Grid,
  Fade,
  CircularProgress,
  Dialog,
  DialogContent,
  Button
} from '@mui/material';
import {
  MusicNote as MusicIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Star as StarIcon
} from '@mui/icons-material';
import { useCountdown } from './useCountdown';
import { useQuizScoring } from '../../../hooks/useQuizScoring';

const QuizQuestion = ({ config, quizData, currentQuizletIndex, onComplete, mainTimer = null, mode = 'normal', flashConfig = null }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [selectedAnswers, setSelectedAnswers] = useState([]); // For multiple choice questions
  const [totalScore, setTotalScore] = useState(0);
  const [questionResults, setQuestionResults] = useState([]);
  const [showScoreSplash, setShowScoreSplash] = useState(false);
  const [lastScoreData, setLastScoreData] = useState(null);
  const [isAnswerLocked, setIsAnswerLocked] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);

  const { calculateScore, getScoreExplanation, formatScore } = useQuizScoring(quizData?.difficulty);

  const currentSong = quizData?.quiz_data?.[currentQuizletIndex];
  const currentQuestion = currentSong?.questions?.[currentQuestionIndex];
  const baseTime = currentQuestion?.base_time || quizData?.difficulty?.base_time || 15;

  // Check if current question is multiple choice
  const isMultipleChoice = currentQuestion?.answer_type_id === 2 || currentQuestion?.answer_type === 'multiple_choice';

  // Debug logging for question type
  // // console.log(`[QuizQuestion] Question type - answer_type_id: ${currentQuestion?.answer_type_id}, answer_type: ${currentQuestion?.answer_type}, isMultipleChoice: ${isMultipleChoice}`);
  // // console.log(`[QuizQuestion] Full question data:`, currentQuestion);

  // Debug logging
  // // console.log(`[QuizQuestion] Debug - currentQuizletIndex: ${currentQuizletIndex}`);
  // // console.log(`[QuizQuestion] Debug - currentSong:`, currentSong);
  // // console.log(`[QuizQuestion] Debug - currentQuestion:`, currentQuestion);
  // // console.log(`[QuizQuestion] Debug - currentQuestionIndex: ${currentQuestionIndex}`);
  // // console.log(`[QuizQuestion] Debug - questions length: ${currentSong?.questions?.length || 0}`);

  // Main timer from QuizMaster
  const mainTimeLeft = mainTimer?.timeLeft || 0;
  const mainTimerRunning = mainTimer?.isRunning || false;
  // const mainTimerDuration = mainTimer?.duration || ((config?.duration_minutes || 5) * 60);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate effective quizlet timer time for flash mode
  const getEffectiveFlashTimerTime = useCallback(() => {
    if (mode !== 'flash' || !flashConfig) return 60;
    // Parent passes remaining seconds for the active quizlet as song_timer_seconds
    const songTimer = typeof flashConfig.song_timer_seconds === 'number' ? flashConfig.song_timer_seconds : 60;
    // // console.log('songTimer', songTimer);
    // // console.log('Main Timer',flashConfig.song_timer_seconds );
    return songTimer;
  }, [mode, flashConfig]);

  // Song/Quizlet timer
  const {
    timeLeft: songTimeLeft,
    start: startSongTimer,
    stop: stopSongTimer,
    getElapsedTime: getSongElapsedTime
  } = useCountdown({
    initialTime: mode === 'flash' ? getEffectiveFlashTimerTime() : 60,
    onComplete: mode === 'flash' ? () => {
      // When the flash quizlet timer expires, close the quizlet without extra scoring
      if (isCompleting) return;

      setIsCompleting(true);
      stopQuestionTimer();

      const totalQuestions = currentSong?.questions?.length || 0;
      const results = {
        quizletIndex: currentQuizletIndex,
        totalScore: totalScore,
        correctAnswers: questionResults.filter(r => r.isCorrect).length,
        totalQuestions: totalQuestions,
        questionResults: questionResults,
        songId: currentSong?.song?.songs_master_id,
        songName: currentSong?.song?.name,
        artist: currentSong?.song?.artist,
        percentage: totalQuestions > 0 ? Math.round((questionResults.filter(r => r.isCorrect).length / totalQuestions) * 100) : 0,
        timeTaken: getSongElapsedTime(),
        timedOut: true
      };

      if (typeof onComplete === 'function') {
        onComplete(currentQuizletIndex, results);
      }
    } : undefined
  });

  // Question timer
  const {
    timeLeft: questionTimeLeft,
    start: startQuestionTimer,
    stop: stopQuestionTimer,
    getElapsedTime,
  } = useCountdown({
    initialTime: baseTime,
    // onComplete: handleQuestionTimeout
  });

  // Derive how long the user spent on the quizlet (total timer - time left)
  const computeQuizletTimeTaken = useCallback(() => {
    const elapsed = typeof getSongElapsedTime === 'function' ? getSongElapsedTime() : 0;
    if (elapsed > 0) {
      return elapsed;
    }
    const totalSeconds = getEffectiveFlashTimerTime();
    if (typeof songTimeLeft === 'number') {
      const diff = totalSeconds - songTimeLeft;
      return diff > 0 ? diff : 0;
    }
    return 0;
  }, [getSongElapsedTime, songTimeLeft, getEffectiveFlashTimerTime]);


  // function handleQuestionTimeout() {
  //   if (!isAnswerLocked) {
  //     // console.log('[QuizQuestion] Question timer expired - auto-advancing');
  //     handleAnswerSubmit(null, true);
  //   } else {
  //     // console.log('[QuizQuestion] Question timer expired but answer is already locked');
  //   }
  // }

  const resetQuestionState = useCallback(() => {
    setSelectedAnswer('');
    setSelectedAnswers([]);
    setIsAnswerLocked(false);
    setIsCompleting(false);
  }, []);

  const resetQuizletState = useCallback(() => {
    // // console.log(`[QuizQuestion] Resetting quizlet state for quizlet ${currentQuizletIndex}`);
    setCurrentQuestionIndex(0);
    setQuestionResults([]);
    setTotalScore(0);
    resetQuestionState();
  }, [resetQuestionState]);

  const moveToNextQuestion = useCallback((updatedQuestionResults, updatedTotalScore) => {
    // Prevent duplicate completion calls
    if (isCompleting) {
      // console.log('[QuizQuestion] Already completing, ignoring duplicate moveToNextQuestion call');
      return;
    }
    
    const totalQuestions = currentSong?.questions?.length || 0;
    // const currentQuestionNumber = currentQuestionIndex + 1; // 1-based indexing for display

    // Check if we should complete the quizlet - use the updated results length instead of current question index
    if (updatedQuestionResults.length >= totalQuestions) {
      // Quizlet complete - all questions answered
      // console.log('[QuizQuestion] All questions answered, completing quizlet');
      setIsCompleting(true);
      stopQuestionTimer();
      stopSongTimer(); // Stop the song timer when quizlet completes
      const quizletTimeTaken = computeQuizletTimeTaken();

      const results = {
        quizletIndex: currentQuizletIndex,
        totalScore: updatedTotalScore,
        correctAnswers: updatedQuestionResults.filter(r => r.isCorrect).length,
        totalQuestions: totalQuestions,
        questionResults: updatedQuestionResults,
        songId: currentSong?.song?.songs_master_id,
        songName: currentSong?.song?.name,
        artist: currentSong?.song?.artist,
        percentage: Math.round((updatedQuestionResults.filter(r => r.isCorrect).length / totalQuestions) * 100),
        timeTaken: quizletTimeTaken,
        timedOut: false
      };

      // Ensure onComplete is called only once
      if (typeof onComplete === 'function') {
        onComplete(currentQuizletIndex, results);
      } else {
        console.error(`[QuizQuestion] onComplete is not a function:`, onComplete);
      }
    } else {
      // Move to next question only if we haven't completed all questions
      const nextQuestionIndex = currentQuestionIndex + 1;
      
      // Safety check: don't increment if we've already answered all questions
      if (nextQuestionIndex >= totalQuestions) {
        // console.log('[QuizQuestion] Next index would exceed total questions, completing instead');
        setIsCompleting(true);
        stopQuestionTimer();
        stopSongTimer();
        
        const quizletTimeTaken = computeQuizletTimeTaken();
        
        const results = {
          quizletIndex: currentQuizletIndex,
          totalScore: updatedTotalScore,
          correctAnswers: updatedQuestionResults.filter(r => r.isCorrect).length,
          totalQuestions: totalQuestions,
          questionResults: updatedQuestionResults,
          songId: currentSong?.song?.songs_master_id,
          songName: currentSong?.song?.name,
          artist: currentSong?.song?.artist,
          percentage: Math.round((updatedQuestionResults.filter(r => r.isCorrect).length / totalQuestions) * 100),
          timeTaken: quizletTimeTaken,
          timedOut: false
        };
        
        if (typeof onComplete === 'function') {
          onComplete(currentQuizletIndex, results);
        }
        return;
      }

      // Stop current timer first
      stopQuestionTimer();

      // Update question index and reset state
      setCurrentQuestionIndex(nextQuestionIndex);
      resetQuestionState();

      // Start timer for next question after a short delay
      setTimeout(() => {
        startQuestionTimer(baseTime);
      }, 100);
    }
  }, [
    currentQuestionIndex,
    currentSong,
    currentQuizletIndex,
    computeQuizletTimeTaken,
    resetQuestionState,
    startQuestionTimer,
    baseTime,
    stopQuestionTimer,
    stopSongTimer,
    onComplete,
    isCompleting
  ]);

  const handleAnswerSubmit = useCallback((answerId, isTimeout = false) => {
    if (isAnswerLocked && !isTimeout) return;
    
    // Check if this is the last question BEFORE processing
    const totalQuestions = currentSong?.questions?.length || 0;
    const isLastQuestion = (currentQuestionIndex + 1) >= totalQuestions;
    
    // If completing or already completed, don't process
    if (isCompleting) {
      // console.log('[QuizQuestion] Already completing, ignoring answer submission');
      return;
    }

    // Stop the question timer
    stopQuestionTimer();
    setIsAnswerLocked(true);

    // Calculate time taken
    const timeTaken = getElapsedTime();

    // Check if answer is correct
    let isCorrect = false;
    if (!isTimeout && answerId) {
      if (isMultipleChoice) {
        // For multiple choice, check if all selected answers are correct
        const correctAnswers = currentQuestion?.answers?.filter(a => a.is_correct === 1).map(a => a.answer_id.toString());
        const selectedAnswersStr = Array.isArray(answerId) ? answerId : [answerId];
        isCorrect = correctAnswers.length === selectedAnswersStr.length &&
          correctAnswers.every(correctId => selectedAnswersStr.includes(correctId));
      } else {
        // For single choice, check if the selected answer is correct
        const correctAnswer = currentQuestion?.answers?.find(a => a.is_correct === 1);
        isCorrect = answerId === correctAnswer?.answer_id?.toString();
      }
    }

    // Calculate score
    const score = calculateScore(isCorrect, timeTaken, baseTime);
    const explanation = getScoreExplanation(isCorrect, timeTaken, baseTime);

    // Record result
    const result = {
      questionId: currentQuestion?.question_id,
      userAnswer: answerId,
      correctAnswer: isMultipleChoice
        ? currentQuestion?.answers?.filter(a => a.is_correct === 1).map(a => a.answer_id.toString())
        : currentQuestion?.answers?.find(a => a.is_correct === 1)?.answer_id?.toString(),
      isCorrect,
      timeTaken,
      score,
      isTimeout
    };

    // Update state and get the new values
    setQuestionResults(prev => {
      const newResults = [...prev, result];

      // Update total score
      setTotalScore(prevScore => {
        const newScore = prevScore + score;

        // Show score splash
        setLastScoreData({
          score,
          explanation,
          isCorrect,
          isTimeout,
          timeTaken
        });
        setShowScoreSplash(true);

        // If this is the last question, complete after showing the result modal
        if (isLastQuestion) {
          // console.log('[QuizQuestion] Last question answered, will complete after showing result');
          stopSongTimer();
          const quizletTimeTaken = computeQuizletTimeTaken();
          
          // Complete after showing the result modal (don't set isCompleting yet)
          setTimeout(() => {
            setShowScoreSplash(false);
            // Now set completing and call onComplete
            setIsCompleting(true);
            const results = {
              quizletIndex: currentQuizletIndex,
              totalScore: newScore,
              correctAnswers: newResults.filter(r => r.isCorrect).length,
              totalQuestions: totalQuestions,
              questionResults: newResults,
              songId: currentSong?.song?.songs_master_id,
              songName: currentSong?.song?.name,
              artist: currentSong?.song?.artist,
              percentage: Math.round((newResults.filter(r => r.isCorrect).length / totalQuestions) * 100),
              timeTaken: quizletTimeTaken,
              timedOut: false
            };
            
            if (typeof onComplete === 'function') {
              onComplete(currentQuizletIndex, results);
            }
          }, 2000); // Show result modal for 2 seconds before completing
        } else {
          // Auto-advance after showing score with updated values
          setTimeout(() => {
            setShowScoreSplash(false);
            moveToNextQuestion(newResults, newScore);
          }, 500);
        }

        return newScore;
      });

      return newResults;
    });

  }, [
    isAnswerLocked,
    stopQuestionTimer,
    getElapsedTime,
    currentQuestion,
    calculateScore,
    getScoreExplanation,
    baseTime,
    moveToNextQuestion,
    isMultipleChoice,
    currentQuestionIndex,
    currentSong,
    currentQuizletIndex,
    stopSongTimer,
    computeQuizletTimeTaken,
    isCompleting
  ]);

  const handleAnswerSelect = (answerId) => {
    if (isAnswerLocked) return;
    setSelectedAnswer(answerId);
    handleAnswerSubmit(answerId);
  };

  const handleCheckboxChange = (answerId) => {
    if (isAnswerLocked) return;
    setSelectedAnswers(prev => {
      if (prev.includes(answerId)) {
        return prev.filter(id => id !== answerId);
      } else {
        return [...prev, answerId];
      }
    });
  };

  const handleMultipleChoiceSubmit = () => {
    if (isAnswerLocked || selectedAnswers.length === 0) return;
    handleAnswerSubmit(selectedAnswers);
  };

  // Reset question index when component mounts or quizlet changes
  useEffect(() => {
    resetQuizletState();
  }, [currentQuizletIndex, resetQuizletState]);

  // (Do not restart on flashConfig change to avoid resetting progress continuously)

  // Start song/quizlet timer when quizlet begins
  useEffect(() => {
    if (currentSong && currentQuizletIndex >= 0) {
      if (mode === 'flash') {
        // For flash mode, start with remaining quizlet time from config
        const effectiveTime = getEffectiveFlashTimerTime();
        stopSongTimer();
        startSongTimer(effectiveTime);
      } else {
        startSongTimer();
      }
    }
  }, [currentQuizletIndex, currentSong, startSongTimer, stopSongTimer, mode, getEffectiveFlashTimerTime]);

  // Safety check to prevent question index from going out of bounds
  // This is a fallback in case moveToNextQuestion doesn't catch it
  useEffect(() => {
    const totalQuestions = currentSong?.questions?.length || 0;
    // Only trigger if we're out of bounds AND not already completing AND we have results for all questions
    if (currentQuestionIndex >= totalQuestions && totalQuestions > 0 && !isCompleting && questionResults.length >= totalQuestions) {
      // console.log('[QuizQuestion] Safety check: Question index out of bounds, completing quizlet');
      setIsCompleting(true);
      stopQuestionTimer();
      stopSongTimer();
      const quizletTimeTaken = computeQuizletTimeTaken();

      const results = {
        quizletIndex: currentQuizletIndex,
        totalScore: totalScore,
        correctAnswers: questionResults.filter(r => r.isCorrect).length,
        totalQuestions: totalQuestions,
        questionResults: questionResults,
        songId: currentSong?.song?.songs_master_id,
        songName: currentSong?.song?.name,
        artist: currentSong?.song?.artist,
        percentage: Math.round((questionResults.filter(r => r.isCorrect).length / totalQuestions) * 100),
        timeTaken: quizletTimeTaken,
        timedOut: false
      };

      if (typeof onComplete === 'function') {
        onComplete(currentQuizletIndex, results);
      }
    }
  }, [currentQuestionIndex, currentSong, isCompleting, onComplete, currentQuizletIndex, totalScore, questionResults, stopQuestionTimer, stopSongTimer, computeQuizletTimeTaken]);

  // Start timer when component mounts or question changes
  useEffect(() => {
    // Don't start timer if we're completing or if there's no current question
    if (isCompleting || !currentQuestion || showScoreSplash) {
      return;
    }
    
    resetQuestionState();

    // Reset and start the question timer
    setTimeout(() => {
      // Double-check we're still not completing
      if (!isCompleting) {
        startQuestionTimer(baseTime);
      }
    }, 500); // Give a moment for UI to render
  }, [currentQuestionIndex, currentQuestion, showScoreSplash, resetQuestionState, startQuestionTimer, baseTime, isCompleting]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      stopQuestionTimer();
      stopSongTimer();
    };
  }, [stopQuestionTimer, stopSongTimer]);

  if (!currentQuestion) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Hide question UI immediately when completing to prevent last question from showing
  // But allow result modal to show if it's still open
  if (isCompleting && !showScoreSplash) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>Completing quizlet...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      maxWidth: 1200,
      mx: 'auto',
      mt: { xs: 1, md: 2 },
      px: { xs: 1, md: 2 },
      maxHeight: { xs: '90vh', md: 'none' },
      overflow: { xs: 'auto', md: 'visible' },
      boxSizing: 'border-box',
    }}>
      {/* Hide question UI when showing result modal for last question */}
      {!showScoreSplash && (
        <>
      {/* Simplified Header - User Info & Score Only */}
      <Box sx={{ mb: { xs: 1.5, md: 3 } }}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: { xs: 1.5, md: 2 },
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, md: 2 }, minWidth: 0 }}>
            <Avatar 
              src={currentSong?.song?.image} 
              sx={{ 
                width: { xs: 42, md: 48 }, 
                height: { xs: 42, md: 48 },
                border: '1px solid rgba(255,255,255,0.2)',
                flexShrink: 0,
              }}
            >
              <MusicIcon sx={{ fontSize: { xs: 16, md: 24 } }} />
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{
                fontWeight: 600,
                color: '#fff',
                fontSize: { xs: '0.8rem', md: '1.25rem' },
                lineHeight: 1.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {currentSong?.song?.name}
              </Typography>
              <Typography variant="body2" sx={{
                color: 'rgba(255,255,255,0.6)',
                fontSize: { xs: '0.65rem', md: '0.875rem' },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {currentSong?.song?.artist}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            <StarIcon sx={{ color: '#fff', fontSize: { xs: 16, md: 24 }, opacity: 0.8 }} />
            <Typography variant="h6" sx={{
              fontWeight: 700,
              color: '#fff',
              fontSize: { xs: '0.85rem', md: '1.25rem' },
            }}>
              {totalScore.toLocaleString()}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* OLD UI - Commented Out */}
      {/* <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2,
        p: 2,
        bgcolor: 'background.paper',
        borderRadius: 2,
        boxShadow: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Avatar src={currentSong?.song?.image} sx={{ mr: 2 }}>
              <MusicIcon />
            </Avatar>
            <Box>
              <Typography variant="h6">{currentSong?.song?.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {currentSong?.song?.artist}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
          <StarIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {totalScore.toLocaleString()}
          </Typography>
        </Box>
      </Box> */}

      {/* OLD UI - Timer Boxes (Commented Out) */}
      {/* Flash mode compact timer cards */}
      {/* {mode === 'flash' && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <Paper sx={{
              p: 2,
              textAlign: 'center',
              bgcolor: songTimeLeft <= 10 ? 'error.light' : (songTimeLeft <= 20 ? 'warning.light' : 'primary.light'),
              color: songTimeLeft <= 10 ? 'error.contrastText' : (songTimeLeft <= 20 ? 'warning.contrastText' : 'primary.contrastText'),
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              minHeight: 120,
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: 3
              }
            }}>
              <Box>
                <Box display="flex" alignItems="center" justifyContent="center" gap={1} mb={1}>
                  <TimerIcon sx={{ fontSize: 22 }} />
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {songTimeLeft}s
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Quizlet Time Remaining
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={(() => {
                    const max = Number(flashBaseTime) > 0 ? Number(flashBaseTime) : (Number(maxFlashTimerTime) > 0 ? Number(maxFlashTimerTime) : 60);
                    const left = Number.isFinite(songTimeLeft) ? songTimeLeft : 0;
                    const pct = (left / max) * 100;
                    return Math.max(0, Math.min(100, pct));
                  })()}
                  sx={{
                    height: 6,
                    backgroundColor: 'rgba(255,255,255,0.3)',
                    borderRadius: 3,
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: 'black',
                      borderRadius: 3
                    }
                  }}
                />
              </Box>
              <Box>
                {songTimeLeft <= 10 && (
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold' }}>
                    ⚠️ Time Ending Soon!
                  </Typography>
                )}
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Paper sx={{
              p: 2,
              textAlign: 'center',
              bgcolor: questionTimeLeft <= 5 ? 'error.light' : (questionTimeLeft <= 10 ? 'warning.light' : 'primary.light'),
              color: questionTimeLeft <= 5 ? 'error.contrastText' : (questionTimeLeft <= 10 ? 'warning.contrastText' : 'primary.contrastText'),
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              minHeight: 120,
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: 3
              }
            }}>
              <Box>
                <Box display="flex" alignItems="center" justifyContent="center" gap={1} mb={1}>
                  <TimerIcon sx={{ fontSize: 22 }} />
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {questionTimeLeft}s
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {questionTimerRunning ? 'Question Time' : 'Timer Stopped'}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={(questionTimeLeft / baseTime) * 100}
                  sx={{
                    height: 6,
                    backgroundColor: 'rgba(255,255,255,0.3)',
                    borderRadius: 3,
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: 'black',
                      borderRadius: 3
                    }
                  }}
                />
              </Box>
              <Box>
                {questionTimeLeft <= 5 && (
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold' }}>
                    ⚠️ Hurry Up!
                  </Typography>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )} */}

      {/* {mode !== 'flash' && (
        <Box sx={{
          mb: 3,
          p: 2,
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 1
        }}>
          <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
            {mode === 'normal' && mainTimerRunning && (
              <Grid item xs={12} sm={4} md={4}>
                <Paper sx={{
                  p: 2,
                  textAlign: 'center',
                  bgcolor: mainTimeLeft <= 60 ? 'error.light' : 'warning.light',
                  color: mainTimeLeft <= 60 ? 'error.contrastText' : 'warning.contrastText',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  minHeight: 140,
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 3
                  }
                }}>
                  <Box>
                    <Box display="flex" alignItems="center" justifyContent="center" gap={1} mb={1}>
                      <TimerIcon sx={{ fontSize: 24 }} />
                      <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                        {formatTime(mainTimeLeft)}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Main Quiz Time
                    </Typography>
                  </Box>
                  <Box>
                    {mainTimeLeft <= 60 && (
                      <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold' }}>
                        ⚠️ Time Running Out!
                      </Typography>
                    )}
                  </Box>
                </Paper>
              </Grid>
            )}

            {(mode === 'normal' || mode === 'flash') && (
              <Grid item xs={12} sm={mainTimerRunning ? 4 : 6} md={mainTimerRunning ? 4 : 6}>
                <Paper sx={{
                  p: 2,
                  textAlign: 'center',
                  bgcolor: songTimeLeft <= 10 ? 'error.light' : 'primary.light',
                  color: songTimeLeft <= 10 ? 'error.contrastText' : 'primary.contrastText',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  minHeight: 140,
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 3
                  }
                }}>
                  <Box>
                    <Box display="flex" alignItems="center" justifyContent="center" gap={1} mb={1}>
                      <TimerIcon sx={{ fontSize: 24 }} />
                      <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                        {songTimeLeft}s
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {mode === 'flash' ? 'Quizlet Time Remaining' : 'Song Time Remaining'}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={(songTimeLeft / (mode === 'flash' ? maxFlashTimerTime : 60)) * 100}
                      sx={{
                        height: 6,
                        backgroundColor: 'rgba(255,255,255,0.3)',
                        borderRadius: 3,
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: 'black',
                          borderRadius: 3
                        }
                      }}
                    />
                  </Box>
                  <Box>
                    {songTimeLeft <= 10 && (
                      <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold' }}>
                        ⚠️ {mode === 'flash' ? 'Time Ending Soon!' : 'Song Ending Soon!'}
                      </Typography>
                    )}
                  </Box>
                </Paper>
              </Grid>
            )}

            <Grid item xs={12} sm={mode === 'normal' ? (mainTimerRunning ? 4 : 6) : 12} md={mode === 'normal' ? (mainTimerRunning ? 4 : 6) : 12}>
              <Paper sx={{
                p: 2,
                textAlign: 'center',
                bgcolor: questionTimeLeft <= 5 ? 'error.light' : 'primary.light',
                color: questionTimeLeft <= 5 ? 'error.contrastText' : 'primary.contrastText',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                minHeight: 140,
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 3
                }
              }}>
                <Box>
                  <Box display="flex" alignItems="center" justifyContent="center" gap={1} mb={1}>
                    <TimerIcon sx={{ fontSize: 24 }} />
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                      {questionTimeLeft}s
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {questionTimerRunning ? 'Question Time' : 'Timer Stopped'}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={(questionTimeLeft / baseTime) * 100}
                    sx={{
                      height: 6,
                      backgroundColor: 'rgba(255,255,255,0.3)',
                      borderRadius: 3,
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: 'black',
                        borderRadius: 3
                      }
                    }}
                  />
                </Box>
                <Box>
                  {questionTimeLeft <= 5 && (
                    <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold' }}>
                      ⚠️ Hurry Up!
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )} */}


      {/* NEW UI - Question Card with Timers Inside */}
      <Card sx={{ 
        mb: { xs: 0.75, md: 3 },
        background: 'rgba(20,0,40,0.8)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: { xs: '12px', md: '16px' },
        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
        position: 'relative',
      }}>
        <CardContent sx={{ 
          p: { xs: 2.5, md: 4 },
          '&:last-child': { pb: { xs: 1.5, md: 4 } },
          position: 'relative',
        }}>
          {/* Box 1: Timers - Top Center on Mobile, Top Right on Desktop */}
          <Box sx={{ 
            display: 'flex',
            flexDirection: 'row',
            gap: { xs: 1, md: 1.5 },
            alignItems: 'center',
            justifyContent: { xs: 'center', sm: 'flex-end' },
            position: { xs: 'static', sm: 'absolute' },
            top: { sm: 16 },
            right: { sm: 16 },
            mb: { xs: 1, sm: 0 },
            zIndex: 10,
          }}>
            {/* Main Quiz Timer (Normal Mode Only) */}
            {mode === 'normal' && mainTimerRunning && (
              <Box sx={{
                bgcolor: 'rgba(0,0,0,0.4)',
                borderRadius: { xs: '6px', md: '8px' },
                px: { xs: 1, md: 1.5 },
                py: { xs: 0.25, md: 0.5 },
                border: '1px solid rgba(255,255,255,0.2)',
              }}>
                <Typography variant="caption" sx={{
                  color: 'rgba(255,255,255,0.6)',
                  fontSize: { xs: '0.55rem', md: '0.7rem' },
                  display: 'block',
                }}>
                  Main
                </Typography>
                <Typography variant="h6" sx={{ 
                  color: mainTimeLeft <= 60 ? '#ff4444' : '#fff', 
                  fontWeight: 700,
                  lineHeight: 1.2,
                  fontSize: { xs: '0.7rem', md: '0.9rem' },
                }}>
                  {formatTime(mainTimeLeft)}
                </Typography>
              </Box>
            )}

            {/* Song/Quizlet Timer */}
            {(mode === 'normal' || mode === 'flash') && (
              <Box sx={{
                bgcolor: 'rgba(0,0,0,0.4)',
                borderRadius: '8px',
                px: 1.5,
                py: 0.5,
                border: '1px solid rgba(255,255,255,0.2)',
              }}>
                <Typography variant="caption" sx={{
                  color: 'rgba(255,255,255,0.6)',
                  fontSize: { xs: '0.55rem', md: '0.7rem' },
                  display: 'block',
                }}>
                  {mode === 'flash' ? 'Quizlet' : 'Song'}
                </Typography>
                <Typography variant="h6" sx={{ 
                  color: songTimeLeft <= 10 ? '#ff4444' : '#fff', 
                  fontWeight: 700,
                  lineHeight: 1.2,
                  fontSize: { xs: '0.75rem', md: '1.25rem' },
                }}>
                  {songTimeLeft}s
                </Typography>
              </Box>
            )}

            {/* Question Timer - Circular */}
            <Box sx={{
              width: { xs: 44, md: 64 },
              height: { xs: 44, md: 64 },
              borderRadius: '50%',
              bgcolor: questionTimeLeft <= 5 ? 'rgba(255,68,68,0.2)' : 'rgba(0,0,0,0.4)',
              border: `2px solid ${questionTimeLeft <= 5 ? '#ff4444' : 'rgba(255,255,255,0.3)'}`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              flexShrink: 0,
            }}>
              <Typography variant="caption" sx={{ 
                color: 'rgba(255,255,255,0.6)', 
                fontSize: { xs: '0.5rem', md: '0.65rem' },
                position: 'absolute',
                top: { xs: 4, md: 8 },
              }}>
                Q
              </Typography>
              <Typography variant="h6" sx={{ 
                color: questionTimeLeft <= 5 ? '#ff4444' : '#fff', 
                fontWeight: 700,
                fontSize: { xs: '0.85rem', md: '1.25rem' },
                lineHeight: 1,
                mt: { xs: 0.25, md: 0.5 },
              }}>
                {questionTimeLeft}s
              </Typography>
            </Box>
          </Box>

          {/* Box 2: Question - Centered */}
          <Box sx={{ 
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: { xs: 'auto', md: '200px' },
            py: { xs: .5, md: 0 },
            pr: { 
              xs: 0, 
              sm: mode === 'normal' && mainTimerRunning ? 24 : 18,
              md: mode === 'normal' && mainTimerRunning ? 28 : 22 
            },
          }}>
            <Typography 
              variant="h4" 
              component="h2"
              sx={{
                color: '#ffffff',
                fontWeight: 600,
                fontSize: { xs: '1.2rem', md: '1.75rem' },
                mb: isMultipleChoice ? { xs: 0.75, md: 2 } : 0,
                lineHeight: { xs: 1.1, md: 1.5 },
              }}
            >
              {currentQuestion.question_text}
            </Typography>
            
            {isMultipleChoice && (
              <Box sx={{ 
                display: 'inline-flex',
                bgcolor: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.2)',
                px: { xs: 1, md: 2 },
                py: { xs: 0.25, md: 0.5 },
                borderRadius: '12px',
                mt: { xs: 0.5, md: 1 },
              }}>
                <Typography variant="body2" sx={{
                  color: '#fff',
                  fontSize: { xs: '0.6rem', md: '0.8rem' },
                  fontWeight: 600,
                }}>
                  MULTIPLE CHOICE
                </Typography>
              </Box>
            )}
          </Box>

          {/* Hint if available */}
          {currentQuestion.question_clue && (
            <Box sx={{
              mb: { xs: 1, md: 3 },
              p: { xs: 1, md: 2 },
              bgcolor: 'rgba(255,255,255,0.05)',
              borderRadius: { xs: '8px', md: '12px' },
              border: '1px solid rgba(255,255,255,0.1)',
              textAlign: 'center',
            }}>
              <Typography variant="body1" sx={{ 
                fontStyle: 'italic',
                color: 'rgba(255,255,255,0.8)',
                fontWeight: 400,
                fontSize: { xs: '0.7rem', md: '1rem' },
                lineHeight: { xs: 1.35, md: 1.5 },
              }}>
                💡 Hint: {currentQuestion.question_clue}
              </Typography>
            </Box>
          )}

          {/* Answer Options in 2x2 Grid */}
          {isMultipleChoice ? (
            <FormGroup>
              <Typography variant="body2" sx={{ 
                mb: { xs: 1, md: 3 },
                fontStyle: 'italic',
                color: 'rgba(255,255,255,0.8)',
                textAlign: 'center',
                fontSize: { xs: '0.65rem', md: '0.875rem' },
                lineHeight: { xs: 1.3, md: 1.5 },
              }}>
                Select all correct answers, then click Submit Answer.
              </Typography>
              
              {/* Options: stacked on mobile, 2x2 on desktop */}
              <Grid container spacing={{ xs: 0.5, md: 2 }}>
                {currentQuestion.answers?.map((answer, index) => (
                  <Grid item xs={12} sm={6} key={answer.answer_id}>
                    <Box
                      onClick={() => !isAnswerLocked && handleCheckboxChange(answer.answer_id.toString())}
                      sx={{
                        p: { xs: 0.75, md: 2.5 },
                        border: '1px solid',
                        borderColor: selectedAnswers.includes(answer.answer_id.toString()) 
                          ? 'rgba(255,255,255,0.4)' 
                          : 'rgba(255,255,255,0.15)',
                        borderRadius: { xs: '8px', md: '12px' },
                        cursor: isAnswerLocked ? 'default' : 'pointer',
                        transition: 'all 0.2s ease',
                        background: selectedAnswers.includes(answer.answer_id.toString())
                          ? 'rgba(255,255,255,0.1)'
                          : 'rgba(255,255,255,0.03)',
                        opacity: isAnswerLocked ? 0.5 : 1,
                        '&:hover': !isAnswerLocked ? {
                          borderColor: 'rgba(255,255,255,0.3)',
                          background: 'rgba(255,255,255,0.08)',
                        } : {}
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, md: 2 } }}>
                        <Checkbox
                          checked={selectedAnswers.includes(answer.answer_id.toString())}
                          onChange={() => handleCheckboxChange(answer.answer_id.toString())}
                          disabled={isAnswerLocked}
                          size="small"
                          sx={{
                            color: 'rgba(255,255,255,0.4)',
                            p: { xs: 0.25, md: 1 },
                            '&.Mui-checked': {
                              color: '#fff',
                            },
                            '& .MuiSvgIcon-root': { fontSize: { xs: 18, md: 24 } },
                          }}
                        />
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            color: '#ffffff',
                            fontWeight: selectedAnswers.includes(answer.answer_id.toString()) ? 600 : 400,
                            fontSize: { xs: '0.75rem', md: '1.05rem' },
                            lineHeight: { xs: 1.3, md: 1.5 },
                          }}
                        >
                          {String.fromCharCode(65 + index)}. {answer.answer_text}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
              
              <Box sx={{ mt: { xs: 1, md: 3 }, textAlign: 'center' }}>
                <Button
                  variant="contained"
                  onClick={handleMultipleChoiceSubmit}
                  disabled={isAnswerLocked || selectedAnswers.length === 0}
                  sx={{ 
                    minWidth: { xs: 140, md: 200 },
                    py: { xs: 0.75, md: 1.5 },
                    px: { xs: 2, md: 4 },
                    background: 'rgba(255,255,255,0.15)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: { xs: '0.75rem', md: '1rem' },
                    borderRadius: { xs: '8px', md: '12px' },
                    border: '1px solid rgba(255,255,255,0.2)',
                    '&:hover': {
                      background: 'rgba(255,255,255,0.2)',
                    },
                    '&:disabled': {
                      background: 'rgba(100,100,100,0.2)',
                      color: 'rgba(255,255,255,0.3)',
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  Submit Answer
                </Button>
              </Box>
            </FormGroup>
          ) : (
            <RadioGroup
              value={selectedAnswer}
              onChange={() => { }} // Controlled by click handler
            >
              {/* Options: stacked on mobile, 2x2 on desktop */}
              <Grid container spacing={{ xs: 1.5, md: 2 }}>
                {currentQuestion.answers?.map((answer, index) => (
                  <Grid item xs={12} sm={6} key={answer.answer_id}>
                    <Box
                      onClick={() => !isAnswerLocked && handleAnswerSelect(answer.answer_id.toString())}
                      sx={{
                        p: { xs: 1.5, md: 2.5 },
                        border: '1px solid',
                        borderColor: selectedAnswer === answer.answer_id.toString() 
                          ? 'rgba(255,255,255,0.4)' 
                          : 'rgba(255,255,255,0.15)',
                        borderRadius: { xs: '10px', md: '12px' },
                        cursor: isAnswerLocked ? 'default' : 'pointer',
                        transition: 'all 0.2s ease',
                        background: selectedAnswer === answer.answer_id.toString()
                          ? 'rgba(255,255,255,0.1)'
                          : 'rgba(255,255,255,0.03)',
                        opacity: isAnswerLocked ? 0.5 : 1,
                        '&:hover': !isAnswerLocked ? {
                          borderColor: 'rgba(255,255,255,0.3)',
                          background: 'rgba(255,255,255,0.08)',
                        } : {}
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, md: 2 } }}>
                        <Radio 
                          value={answer.answer_id.toString()}
                          disabled={isAnswerLocked}
                          checked={selectedAnswer === answer.answer_id.toString()}
                          size="small"
                          sx={{
                            color: 'rgba(255,255,255,0.4)',
                            p: { xs: 0.25, md: 1 },
                            '&.Mui-checked': {
                              color: '#fff',
                            },
                            '& .MuiSvgIcon-root': { fontSize: { xs: 18, md: 24 } },
                          }}
                        />
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            color: '#ffffff',
                            fontWeight: selectedAnswer === answer.answer_id.toString() ? 600 : 400,
                            fontSize: { xs: '0.95rem', md: '1.05rem' },
                            lineHeight: { xs: 1.3, md: 1.5 },
                          }}
                        >
                          {String.fromCharCode(65 + index)}. {answer.answer_text}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </RadioGroup>
          )}
        </CardContent>
      </Card>

      {/* OLD UI - Question Card (Commented Out) */}
      {/* <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="h5" component="h2">
              {currentQuestion.question_text}
            </Typography>
            {isMultipleChoice && (
              <Typography variant="body2" sx={{
                bgcolor: 'primary.light',
                color: 'primary.contrastText',
                px: 1,
                py: 0.5,
                borderRadius: 1,
                fontSize: '0.75rem',
                fontWeight: 'bold'
              }}>
                MULTIPLE CHOICE
              </Typography>
            )}
          </Box>

          {currentQuestion.question_clue && (
            <Box sx={{
              mb: 3,
              p: 2,
              bgcolor: 'info.light',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'info.main'
            }}>
              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                💡 Hint: {currentQuestion.question_clue}
              </Typography>
            </Box>
          )}

          {isMultipleChoice ? (
            <FormGroup>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                Select all correct answers, then click Submit Answer.
              </Typography>
              {currentQuestion.answers?.map((answer) => (
                <FormControlLabel
                  key={answer.answer_id}
                  control={
                    <Checkbox
                      checked={selectedAnswers.includes(answer.answer_id.toString())}
                      onChange={() => handleCheckboxChange(answer.answer_id.toString())}
                      disabled={isAnswerLocked}
                    />
                  }
                  label={answer.answer_text}
                  sx={{
                    mb: 1,
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    cursor: isAnswerLocked ? 'default' : 'pointer',
                    opacity: isAnswerLocked ? 0.7 : 1,
                    '&:hover': !isAnswerLocked ? {
                      bgcolor: 'action.hover'
                    } : {}
                  }}
                />
              ))}
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleMultipleChoiceSubmit}
                  disabled={isAnswerLocked || selectedAnswers.length === 0}
                  sx={{ minWidth: 120 }}
                >
                  Submit Answer
                </Button>
              </Box>
            </FormGroup>
          ) : (
            <RadioGroup
              value={selectedAnswer}
              onChange={() => { }}
            >
              {currentQuestion.answers?.map((answer) => (
                <FormControlLabel
                  key={answer.answer_id}
                  value={answer.answer_id.toString()}
                  control={<Radio disabled={isAnswerLocked} />}
                  label={answer.answer_text}
                  onClick={() => handleAnswerSelect(answer.answer_id.toString())}
                  sx={{
                    mb: 1,
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    cursor: isAnswerLocked ? 'default' : 'pointer',
                    opacity: isAnswerLocked ? 0.7 : 1,
                    '&:hover': !isAnswerLocked ? {
                      bgcolor: 'action.hover'
                    } : {}
                  }}
                />
              ))}
            </RadioGroup>
          )}
        </CardContent>
      </Card> */}
        </>
      )}

      {/* Score Splash - Simplified */}
      <Dialog 
        open={showScoreSplash} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(20,0,40,0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid',
            borderColor: lastScoreData?.isCorrect ? 'rgba(255,255,255,0.2)' : 'rgba(255,68,68,0.3)',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
          }
        }}
      >
        <DialogContent sx={{ textAlign: 'center', py: 5, px: 4 }}>
          {lastScoreData && (
            <Fade in={showScoreSplash}>
              <Box>
                {lastScoreData.isCorrect ? (
                  <CheckIcon sx={{ 
                    fontSize: 80, 
                    color: '#fff', 
                    mb: 2,
                  }} />
                ) : (
                  <CancelIcon sx={{ 
                    fontSize: 80, 
                    color: '#ff4444', 
                    mb: 2,
                  }} />
                )}

                <Typography variant="h4" gutterBottom sx={{ 
                  color: '#ffffff',
                  fontWeight: 600,
                  mb: 2
                }}>
                  {lastScoreData.explanation}
                </Typography>

                <Typography variant="h2" sx={{
                  color: lastScoreData.score > 0 ? '#fff' : '#ff4444',
                  fontWeight: 700,
                  mb: 2
                }}>
                  {formatScore(lastScoreData.score)}
                </Typography>

                <Box sx={{
                  bgcolor: 'rgba(255,255,255,0.05)',
                  borderRadius: '12px',
                  py: 1,
                  px: 2,
                  display: 'inline-block'
                }}>
                  <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    ⏱ Time: {lastScoreData.timeTaken.toFixed(1)}s
                  </Typography>
                </Box>
              </Box>
            </Fade>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default QuizQuestion;